import { useEffect, useState } from "react";
import { Swiper as SwiperInstance } from "swiper";
import { EffectCreative } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";
import "swiper/css/effect-creative";
import "./custom.css";

import { useLocaleCtx } from "@/contexts/locale";
import { useWindowSize } from "@/hooks/use-window-size";
import { cn } from "@/lib/utils";
import { NomineesBeResponse, NomineesProps } from "@/services/nominees/nominees.server";
import { Skeleton } from "../ui/skeleton";
import Card, { CardDetail, CardDetailSkeleton } from "./card";
import { useNomineesCtx } from "./contexts/nomination";
import { Image } from "../shared/image";

export function NavigationButtons({ swiper }: { swiper: SwiperInstance }) {
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const { loading } = useNomineesCtx();

  useEffect(() => {
    swiper.on("reachEnd", () => setIsEnd(true));
    swiper.on("reachBeginning", () => setIsBeginning(true));

    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
    return () => {
      swiper.off("reachEnd");
      swiper.off("reachBeginning");
    };
  }, [swiper, swiper.isEnd, swiper.isBeginning]);

  if (loading) {
    return (
      <div className="absolute bottom-60 left-1/2 z-10 flex w-[90%] -translate-x-1/2 justify-between sm:w-[50%] lg:bottom-44 lg:w-full rtl:flex-row-reverse">
        <Skeleton className="h-10 w-10 rounded-full" />
        <Skeleton className="h-10 w-10 rounded-full" />
      </div>
    );
  }

  return (
    <div className="absolute bottom-60 left-1/2 z-10 flex w-[90%] -translate-x-1/2 justify-between sm:w-[50%] lg:bottom-44 lg:w-full rtl:flex-row-reverse">
      <Image
        src="/assets/svgs/left-arrow-dark.svg"
        width={58}
        height={58}
        alt="left arrow"
        className={cn(
          "cursor-pointer select-none text-secondary",
          isBeginning ? "cursor-not-allowed opacity-40" : ""
        )}
        onClick={() => !isBeginning && swiper.slidePrev()}
      />
      <Image
        src="/assets/svgs/right-arrow-dark.svg"
        width={58}
        height={58}
        alt="right arrow"
        className={cn(
          "cursor-pointer select-none text-secondary",
          isEnd ? "cursor-not-allowed opacity-40" : ""
        )}
        onClick={() => !isEnd && swiper.slideNext()}
      />
    </div>
  );
}

function generateKey(width: number) {
  if (width >= 1024) return "lg";

  return "base";
}

function Slider({ nominees }: { nominees: NomineesBeResponse["data"] | [] }) {
  const [activeCard, setActiveCard] = useState<NomineesProps>();
  const [swiper, setSwiper] = useState<SwiperInstance | null>(null);
  const { loading } = useNomineesCtx();

  const { lang } = useLocaleCtx();

  const { width } = useWindowSize();
  const key = generateKey(width);

  const nomineesWithPlaceholders = nominees.length ? nominees.slice() : new Array(3).fill({});

  const isLTR = lang === "en";
  const startSlide = isLTR ? 0 : nomineesWithPlaceholders.length - 1;

  if (!isLTR) nomineesWithPlaceholders.reverse();

  const handleSlideChange = (swiper: SwiperInstance) => {
    if (nominees.length === 0) return;
    const activeIndex = swiper.activeIndex;
    setActiveCard(nomineesWithPlaceholders[activeIndex]);
  };

  useEffect(() => {
    if (swiper) {
      swiper.changeLanguageDirection("ltr");
    }
  }, [swiper]);

  useEffect(() => {
    if (nominees.length > 0) setActiveCard(nomineesWithPlaceholders[startSlide]);
    if (swiper) {
      swiper.slideTo(startSlide, 0);
    }
  }, [nominees, swiper]);

  return (
    <div className="relative lg:mx-auto lg:grid lg:max-w-[85%] lg:grid-cols-2 lg:items-center lg:justify-start 2xl:max-w-[90%]">
      <Swiper
        key={key}
        grabCursor={true}
        modules={[EffectCreative]}
        effect={"creative"}
        speed={1000}
        initialSlide={0}
        breakpoints={{
          1024: {
            slidesPerView: 1,
            centeredSlides: false,
            direction: "vertical",
            autoHeight: true,
            creativeEffect: {
              limitProgress: 2,
              perspective: false,
              prev: {
                scale: 0.6,
                translate: [0, "-35%", 0],
                opacity: 0.2,
              },
              next: {
                scale: 0.6,
                translate: [0, "35%", 0],
                opacity: 0.2,
              },
            },
          },
          640: {
            slidesPerView: 2,
            centeredSlides: true,
            creativeEffect: {
              limitProgress: 2,
              perspective: false,
              prev: {
                scale: 0.6,
                translate: ["-55%", 0, 0],
                opacity: 0.2,
              },
              next: {
                scale: 0.6,
                translate: ["55%", 0, 0],
                opacity: 0.2,
              },
            },
          },
          0: {
            slidesPerView: 1.3,
            centeredSlides: true,
            creativeEffect: {
              limitProgress: 2,
              perspective: false,
              prev: {
                scale: 0.6,
                translate: ["-55%", 0, 0],
                opacity: 0.2,
              },
              next: {
                scale: 0.6,
                translate: ["55%", 0, 0],
                opacity: 0.2,
              },
            },
          },
        }}
        onSlideChange={handleSlideChange}
        onSwiper={(swiper: SwiperInstance) => setSwiper(swiper)}
        className="!h-full md:w-[70%] lg:!mx-0 lg:w-[70%] lg:!overflow-visible 2xl:w-[66%]"
      >
        {nomineesWithPlaceholders?.map((nom, index: number) => {
          return (
            <SwiperSlide key={index} className="!h-auto">
              <div className="">
                <Card isWinner={nom.isWinner} src={nom.image_link} {...nom} />
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
      <div className="relative">
        {activeCard && !loading && (
          <CardDetail
            isWinner={activeCard.isWinner}
            isActive={true}
            key={activeCard.name}
            name={activeCard.name}
            title={activeCard.title}
            description={activeCard.description}
            year={activeCard.year}
          />
        )}
        {loading && <CardDetailSkeleton />}
        {swiper && <NavigationButtons swiper={swiper} />}
      </div>
    </div>
  );
}

export default Slider;
